/**
 * Native-auth compatibility shim (Firebase has been removed).
 *
 * The app now authenticates against the backend's native JWT endpoints
 * (`/auth/login`, `/auth/register`, `/auth/profile`). To avoid rewriting every
 * call site that used `auth.currentUser.getIdToken()`, this exposes a tiny
 * `auth` object whose `currentUser` is derived from the JWT stored in
 * localStorage, and whose `getIdToken()` returns that JWT.
 *
 * The Firestore/Storage exports (`db`, `storage`) are intentionally undefined —
 * data goes through the backend API, uploads through the backend upload routes.
 */

export interface AppUser {
  uid: string;
  email: string;
  displayName?: string;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
}

export const TOKEN_KEY = 'authToken';
const UID_KEY = 'authUid';
const EMAIL_KEY = 'authEmail';
const NAME_KEY = 'authName';

function buildUser(): AppUser | null {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  return {
    uid: localStorage.getItem(UID_KEY) || '',
    email: localStorage.getItem(EMAIL_KEY) || '',
    displayName: localStorage.getItem(NAME_KEY) || '',
    getIdToken: async () => localStorage.getItem(TOKEN_KEY) || '',
  };
}

class AuthCompat {
  /** Current user, derived from the stored JWT (null when logged out). */
  get currentUser(): AppUser | null {
    return buildUser();
  }

  /** Persist identity so `currentUser` and `getIdToken()` resolve correctly. */
  setSession(token: string, user: { id?: string; uid?: string; email?: string; firstName?: string; lastName?: string }): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(UID_KEY, user.id || user.uid || '');
    localStorage.setItem(EMAIL_KEY, user.email || '');
    localStorage.setItem(NAME_KEY, `${user.firstName || ''} ${user.lastName || ''}`.trim());
  }

  async signOut(): Promise<void> {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(UID_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(NAME_KEY);
  }
}

export const firebaseEnabled = false;
export const auth = new AuthCompat();
export const db: undefined = undefined;
export const storage: undefined = undefined;
export default null;
